import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, getOrgInfo, requestPasswordReset, loginTwoFactor } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { formatDate, formatDateTime } from '../utils/formatters';
import '../components/common/ui.css';
import './Login.css';

/* Тот же глаз, что и в SecurityForm.jsx (смена пароля в профиле) —
   на экране входа человеку особенно важно видеть, что он набрал. */
// Шапочка повара вместо эмодзи 🍽️ — сервис для ресторанов/общепита.
const ChefHatIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 14.584C3.2341 13.8124 2 12.0503 2 10C2 7.23858 4.23858 5 7 5C7.25052 5 7.49673 5.01842 7.73736 5.05399" />
    <path d="M19 14.584V18C19 19.8856 19 20.8285 18.4142 21.4142C17.8284 22 16.8856 22 15 22H9C7.11438 22 6.17157 22 5.58579 21.4142C5 20.8285 5 19.8856 5 18V14.584" />
    <path d="M16.2627 5.05399C16.5033 5.01842 16.7495 5 17.0001 5C19.7615 5 22.0001 7.23858 22.0001 10C22.0001 12.0503 20.766 13.8124 19.0001 14.584" />
    <path d="M16.5 7V6.5C16.5 5.99416 16.4165 5.50782 16.2626 5.05399C15.6604 3.27806 13.9794 2 12 2C10.0206 2 8.33962 3.27806 7.73736 5.05399C7.58346 5.50782 7.5 5.99416 7.5 6.5V7" />
    <path d="M5 18H19" />
  </svg>
);

const EyeIcon = ({ open }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M3.27489 15.2957C2.42496 14.1915 2 13.6394 2 12C2 10.3606 2.42496 9.80853 3.27489 8.70433C4.97196 6.49956 7.81811 4 12 4C16.1819 4 19.028 6.49956 20.7251 8.70433C21.575 9.80853 22 10.3606 22 12C22 13.6394 21.575 14.1915 20.7251 15.2957C19.028 17.5004 16.1819 20 12 20C7.81811 20 4.97196 17.5004 3.27489 15.2957Z" />
        <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" />
      </>
    ) : (
      <path d="M12 14C5 14 2 7 2 7M22 7C22 7 21.0586 9.19661 19 11.1288C18.0872 11.9856 16.9547 12.7904 15.5872 13.3287C14.5334 13.7435 13.34 14 12 14M12 14V16.5M15.5872 13.3287L17 15.5M19 11.1288L20.5 12.6288M8.41281 13.3287L7 15.5M5 11.1288L3.5 12.6288" />
    )}
  </svg>
);

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null); // null пока грузится
  // 'login' | 'forgot' — заявка на восстановление показывается прямо здесь,
  // отдельной страницы для неё нет: человек попадает сюда ровно в тот
  // момент, когда не смог войти.
  const [mode, setMode] = useState('login');
  const [resetSent, setResetSent] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    getOrgInfo().then(setOrg).catch(() => setOrg({ found: false }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(username, password);

      if (data.totp_required) {
        // Пароль верный, но сессии ещё НЕТ — она появится только после
        // второго шага. Поэтому checkAuth здесь не вызываем и никуда не
        // переходим: просто показываем поле для кода.
        setMode('totp');
        setPassword('');
        return;
      }

      if (data.success) {
        // Сессия на сервере уже есть, но в AuthProvider isAuthenticated
        // ещё false — без этого ProtectedRoute вернёт нас обратно на /login.
        await checkAuth();
        navigate(data.user?.role === 'superadmin' ? '/platform' : '/');
      } else {
        setError(data.error || 'Неверный логин или пароль');
      }
    } catch (err) {
      console.error('Login error:', err);
      // axios бросает исключение на любой не-2xx, включая штатный 401/402/404.
      // Показываем реальную причину от сервера, а «нет связи» —
      // только когда ответа действительно не было.
      if (err.response) {
        setError(err.response.data?.error || 'Неверный логин или пароль');
      } else {
        setError('Ошибка соединения с сервером');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginTwoFactor(totpCode);
      if (data.success) {
        await checkAuth();
        navigate(data.user?.role === 'superadmin' ? '/platform' : '/');
      } else {
        setError(data.error || 'Неверный код');
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || 'Неверный код');
      } else {
        setError('Ошибка соединения с сервером');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(username);
      // Ответ намеренно одинаковый и для найденного, и для несуществующего
      // пользователя — сервер не подтверждает существование учётной записи,
      // и экран не должен делать это за него.
      setResetSent(true);
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || 'Не удалось отправить заявку');
      } else {
        setError('Ошибка соединения с сервером');
      }
    } finally {
      setLoading(false);
    }
  };

  // Организация ещё не определилась по поддомену — не мигаем формой раньше времени
  if (org === null) {
    return <div className="login-container" />;
  }

  if (!org.platform && !org.found) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1><span>Организация не найдена</span></h1>
          <div className="subtitle">Проверьте адрес — такого поддомена нет на платформе.</div>
        </div>
      </div>
    );
  }

  if (!org.platform && org.status === 'suspended') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1><span>{org.name}</span></h1>
          <div className="subtitle">Вход недоступен</div>
        </div>
        <div className="ui-modal" role="presentation">
          <div className="ui-modal-box" role="dialog" aria-modal="true">
            <h2 className="ui-text-danger">Аккаунт заблокирован</h2>
            <p className="login-blocked-reason">
              {org.suspended_reason || 'Обратитесь к администратору платформы.'}
            </p>
            {org.suspended_at && (
              <p className="ui-dim">Заблокирован: {formatDateTime(org.suspended_at)}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>
          <span className="logo-icon"><ChefHatIcon /></span>
          <span>{org.platform ? 'Платформа' : org.name}</span>
        </h1>
        <div className="subtitle">Войдите в систему</div>
        {!org.platform && org.status === 'trial' && org.trial_ends_at && (
          <div className="subtitle">Пробный период до {formatDate(org.trial_ends_at)}</div>
        )}

        {mode === 'totp' ? (
          <form onSubmit={handleTotp}>
            <div className="subtitle login-hint">
              Введите код из приложения-аутентификатора. Если телефона нет под
              рукой — подойдёт любой из резервных кодов.
            </div>
            <div className="input-group">
              <label htmlFor="login-totp">Код подтверждения</label>
              <input
                id="login-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="6 цифр или резервный код"
                autoFocus
                required
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Проверяем...' : 'Войти'}
            </button>
            {error && <div className="error" role="alert">{error}</div>}
            <button
              type="button"
              className="login-link"
              onClick={() => { setMode('login'); setError(''); setTotpCode(''); }}
            >
              Начать вход заново
            </button>
          </form>
        ) : mode === 'forgot' ? (
          resetSent ? (
            <>
              <div className="subtitle login-hint">
                Если такой пользователь есть и у него указана почта, письмо со
                ссылкой уже отправлено. Ссылка действует час и сработает один раз.
              </div>
              <button type="button" onClick={() => { setMode('login'); setResetSent(false); }}>
                Вернуться ко входу
              </button>
            </>
          ) : (
            <form onSubmit={handleForgot}>
              <div className="input-group">
                <label htmlFor="login-forgot-username">Логин или email</label>
                <input
                  id="login-forgot-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Что помните"
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? 'Отправляем...' : 'Прислать ссылку'}
              </button>
              {error && <div className="error" role="alert">{error}</div>}
              <button
                type="button"
                className="login-link"
                onClick={() => { setMode('login'); setError(''); }}
              >
                Вспомнил пароль — войти
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="login-username">Логин</label>
              <input
                id="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Введите логин"
                autoComplete="username"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="login-password">Пароль</label>
              <div className="ui-input-wrap">
                <input
                  id="login-password"
                  type={passwordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="ui-eye"
                  onClick={() => setPasswordVisible(v => !v)}
                  aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  <EyeIcon open={passwordVisible} />
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Вход...' : 'Войти'}
            </button>
            {error && <div className="error" role="alert">{error}</div>}
            <button
              type="button"
              className="login-link"
              onClick={() => { setMode('forgot'); setError(''); setPassword(''); }}
            >
              Забыли пароль?
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;